const { getElement, getElements } = window.BAO.Utils

/**
 * @typedef {Object} SelectedVariantOption
 *
 * @property {string} label
 * @property {string} value
 */

const selectors = {
  addToCart: '[data-product-add-to-cart]',
  addToCartText: '[data-product-add-to-cart-text]',
  fixedAddToCart: '[data-product-fixed-add-to-cart]',
  fixedMakeSelection: '[data-product-fixed-make-selection]',
  fixedPrice: '[data-product-fixed-price]',
  form: '[data-product-form]',
  orderDeadline: '.prd-OrderDeadline',
  price: '[data-product-price]',
  productATCPanel: '[data-product-atc-panel]',
  productBISPanel: '[data-product-bis-panel]',
  productBISForm: '[data-product-bis-el="form"]',
  productBISSuccess: '[data-product-bis-el="success"]',
  productStockMessage: '[data-product-stock]',
  sku: '[data-product-sku]',
}

class Variant {
  constructor (data, element) {
    /**
     * @type {ShopifyVariant}
     * @public
     */
    this.data = data
    /**
     * @type {VariantOptions}
     * @public
     */
    this.element = element
  }

  render () {
    this.setAvailability()
    this.setPrice()
    this.updateInput()
    this.updateStockMessage()
  }

  async setPrice () {
    await window.BAO.sectionRenderer
      .fetch('product-price', `${this.url.toString()}&section_id=product-price`)
      .then(res => {
        const sectionHTML = window.BAO.sectionRenderer.getInnerHtml(
          res['product-price'],
        )

        const priceElements = getElements(selectors.price)
        priceElements.forEach(el => {
          el.innerHTML = sectionHTML
        })
      })

    await window.BAO.sectionRenderer
      .fetch(
        'product-price-fixed',
        `${this.url.toString()}&section_id=product-price-fixed`,
      )
      .then(res => {
        const sectionHTML = window.BAO.sectionRenderer.getInnerHtml(
          res['product-price-fixed'],
        )

        const fixedPriceElements = getElements(selectors.fixedPrice)
        fixedPriceElements.forEach(el => {
          el.innerHTML = sectionHTML
        })
      })
  }

  setAvailability () {
    getElements(selectors.addToCart).forEach(
      el => (el.disabled = !this.data.available),
    )

    getElements(selectors.orderDeadline).forEach(el =>
      el.setAttribute('aria-hidden', !this.data.available),
    )

    const hasSellingPlan = this.data?.selling_plan_allocations?.length > 0
    getElements(selectors.addToCartText).forEach(el => {
      const addToCartText = hasSellingPlan
        ? window.theme.strings.preOrder
        : window.theme.strings.addToCart
      const buttonText = this.data.available
        ? addToCartText
        : window.theme.strings.soldOut

      el.textContent = buttonText
      el.setAttribute('data-text', buttonText)
    })

    getElements(selectors.productATCPanel).forEach(el =>
      el.setAttribute('aria-hidden', !this.data.available),
    )
    getElements(selectors.productBISPanel).forEach(el =>
      el.setAttribute('aria-hidden', this.data.available),
    )
    getElements(selectors.productBISForm).forEach(el => {
      el.setAttribute('data-variant-id', this.data.id)
      el.setAttribute('aria-hidden', false)
    })
    getElements(selectors.productBISSuccess).forEach(el =>
      el.setAttribute('aria-hidden', true),
    )

    getElements(selectors.fixedAddToCart).forEach(el =>
      el.setAttribute('aria-hidden', false),
    )
    getElements(selectors.fixedMakeSelection).forEach(el =>
      el.setAttribute('aria-hidden', true),
    )
  }

  updateInput () {
    const inputs = getElements(`form[action*="/cart/add"] [name="id"]`)

    inputs.forEach(input => {
      input.value = this.id
      input.dispatchEvent(new Event('change', { bubbles: true }))
    })
  }

  async updateStockMessage () {
    const stockElement = getElement(selectors.productStockMessage)
    const hasSellingPlan = stockElement?.dataset?.hasSellingPlan

    if (hasSellingPlan) return

    await window.BAO.sectionRenderer
      .fetch(
        'product-stock-message',
        `${this.url.toString()}&section_id=product-stock-message`,
      )
      .then(res => {
        const sectionHTML = window.BAO.sectionRenderer.getInnerHtml(
          res['product-stock-message'],
        )

        stockElement.innerHTML = sectionHTML
      })
  }

  /**
   * @param {string} name
   * @param {string} value
   *
   * @returns {void}
   */
  updateIndicator (name, value) {
    const sizeOptionEl = this.element.els.option.elements.find(
      el => el.dataset.name.toLowerCase() === 'size',
    )
    const sizeOptionTextEl = sizeOptionEl.querySelector(
      '[data-variant-radios-el="option-text"]',
    )
    if (sizeOptionEl && sizeOptionTextEl) {
      sizeOptionTextEl.innerText = window.theme.strings.size
      sizeOptionEl.classList.remove('prd-DetailVariants_Row-required')
    }

    if (!this.element.els['variant-indicator']?.exists) return

    const indicator = this.element.els['variant-indicator'].elements.find(
      el => el.dataset.name === name,
    )
    if (!indicator) return

    if (name.toLowerCase() === 'size') {
      indicator.textContent = `: ${
        window.sizeStrings[value.toLowerCase()]
          ? window.sizeStrings[value.toLowerCase()]
          : value
      }`
    } else {
      indicator.textContent = `: ${value}`
    }
  }

  setUrl () {
    window.history.replaceState(
      { path: this.url.toString() },
      '',
      this.url.toString(),
    )
  }

  setShareUrl () {
    const shareButtons = getElements('share-button')
    if (shareButtons.length === 0) return

    shareButtons.forEach(shareButton => {
      shareButton.updateUrl(this.url.toString())
    })
  }

  /**
   * @returns {number}
   */
  get id () {
    return this.data.id
  }

  /**
   * @returns {URL}
   */
  get url () {
    // eslint-disable-next-line compat/compat
    const params = new URLSearchParams(window.location.search)
    params.set('variant', this.id.toString())

    // eslint-disable-next-line compat/compat
    const url = new URL(window.location.href)
    url.search = params.toString()

    return url
  }
}

export class VariantOptions extends window.BAO.CustomElement() {
  static get requiredElements () {
    return ['data', 'option', 'option-choice']
  }

  constructor () {
    super()

    /**
     * @type {Variant[]}
     * @public
     */
    this.collectedVariants = [new Variant(this.defaultVariant, this)]
  }

  setupListeners () {
    super.setupListeners()

    this.onVariantChange = this.onVariantChange.bind(this)

    this.listeners.add(this, 'change', this.onVariantChange)

    window.addEventListener('variant:reload', e => {
      this.getVariant().render()
    })
  }

  onVariantChange (e) {
    const variant = this.getVariant()

    variant.render()
    variant.updateIndicator(e.target.name, e.target.dataset.value)

    if (!this.currentVariant) {
      this.resetPage(variant)

      return
    }

    variant.setUrl()
    variant.setShareUrl()

    const customEvent = new CustomEvent('variant:changed')
    window.dispatchEvent(customEvent)
  }

  /**
   * @param {Variant} variant
   *
   * @returns {void}
   */
  resetPage (variant) {
    window.history.replaceState(
      { path: window.location.pathname },
      '',
      window.location.pathname,
    )
  }

  /**
   * @param {ShopifyVariant} data
   *
   * @returns {Variant}
   */
  createVariant (data) {
    const variant = new Variant(data, this)
    this.collectedVariants.push(variant)

    return variant
  }

  /**
   * @returns {Variant}
   */
  getVariant () {
    if (!this.currentVariant) {
      return this.collectedVariants[0]
    }

    const existingVariant = this.collectedVariants.find(
      variant => variant.id === this?.currentVariant?.id,
    )
    if (existingVariant) return existingVariant

    return this.createVariant(this.currentVariant)
  }

  /**
   * @returns {SelectedVariantOption[]}
   */
  get selectedOptions () {
    this.throwError(`selectedOptions needs to be implemented on this class`)

    return []
  }

  /**
   * @returns {ShopifyVariant}
   */
  get defaultVariant () {
    return this.data[0]
  }

  /**
   * @returns {ShopifyVariant|undefined}
   */
  get currentVariant () {
    return this.data.find(variant => {
      return variant.options.every(
        (option, index) => this.selectedOptions[index].value === option,
      )
    })
  }

  /**
   * @returns {ShopifyVariant[]}
   */
  get data () {
    return JSON.parse(this.els.data.element.innerText)
  }
}

export class VariantRadios extends VariantOptions {
  get selectedOptions () {
    return this.els.option.elements.map(el => {
      const { name } = el.dataset
      const choice = getElements(this.els['option-choice'].selector, {
        context: el,
      }).find(el => el.checked)?.value

      return {
        label: name,
        value: choice,
      }
    })
  }
}

export class VariantSelects extends VariantOptions {
  get selectedOptions () {
    return this.els.option.elements.map(el => {
      const { name } = el.dataset

      return {
        label: name,
        value: el.querySelector('select').value,
      }
    })
  }
}

if (!customElements.get('variant-radios')) {
  customElements.define('variant-radios', VariantRadios)
}

if (!customElements.get('variant-selects')) {
  customElements.define('variant-selects', VariantSelects)
}
